import { useEffect, useState } from 'react';
import HttpClient from '../../helpers/HttpClient';
import { TrainingEvent } from '../../types/training-event.type';
import { Person } from '../../types/person.type';
import { MatchFilterOptions, SeasonLeaders } from './homeTypes';

const TEAM_FILTER_KEY = 'member_team_filter';

function readStoredList(key: string): string[] {
  try { return JSON.parse(localStorage.getItem(key) ?? '[]'); } catch { return []; }
}

export function writeStoredList(key: string, value: string[]) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage unavailable */ }
}

/** All training events, sorted by date/start time. Shared by the RSVP lists and the calendar. */
export function useTrainings(currentPerson: Person | null) {
  const [trainings, setTrainings] = useState<TrainingEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    new HttpClient<TrainingEvent>('/training-event').getAll()
      .catch(() => [] as TrainingEvent[])
      .then((all) => {
        setTrainings([...all].sort((a, b) => a.date.localeCompare(b.date) || a.startTime - b.startTime));
      })
      .finally(() => setLoading(false));
  }, [currentPerson]);

  return { trainings, loading };
}

export interface TeamFilter {
  allTeams: string[];
  selectedTeams: string[];
  toggleTeam: (team: string) => void;
}

/** Team filter shared by the calendar and the season leaderboards; persisted per browser. */
export function useTeamFilter(): TeamFilter {
  const [allTeams, setAllTeams] = useState<string[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>(() => readStoredList(TEAM_FILTER_KEY));

  useEffect(() => {
    new HttpClient<MatchFilterOptions>('/match/filter-options').getSingle()
      .catch(() => null)
      .then((options) => {
        const teams = options?.teams ?? [];
        setAllTeams(teams);
        setSelectedTeams((prev) => prev.filter((t) => teams.includes(t)));
      });
  }, []);

  const toggleTeam = (team: string) => {
    const next = toggleInSelection(allTeams, selectedTeams, team);
    setSelectedTeams(next);
    writeStoredList(TEAM_FILTER_KEY, next);
  };

  return { allTeams, selectedTeams, toggleTeam };
}

const EMPTY_LEADERS: SeasonLeaders = { topScorers: [], topCards: [], topOwnGoals: [] };

/** Season leaderboards (scorers, cards, own goals), scoped to the selected teams. */
export function useSeasonLeaders(selectedTeams: string[]): SeasonLeaders {
  const [leaders, setLeaders] = useState<SeasonLeaders>(EMPTY_LEADERS);

  // Depends on the joined string, not the array reference, so it doesn't refire on every render.
  const selectedTeamsKey = selectedTeams.join(',');
  useEffect(() => {
    const params = selectedTeamsKey ? { teams: selectedTeamsKey } : undefined;
    new HttpClient<SeasonLeaders>('/match/season-leaders')
      .getSingle(params)
      .then((res) => setLeaders({
        topScorers: res?.topScorers ?? [],
        topCards: res?.topCards ?? [],
        topOwnGoals: res?.topOwnGoals ?? [],
      }))
      .catch(() => setLeaders(EMPTY_LEADERS));
  }, [selectedTeamsKey]);

  return leaders;
}

// Toggle semantics shared by both filters: an empty selection means "all";
// deselecting from "all" selects everything else; selecting the last missing
// item collapses back to "all" (empty).
export function toggleInSelection(all: string[], selected: string[], value: string): string[] {
  if (selected.length === 0) return all.filter((v) => v !== value);
  if (selected.includes(value)) {
    const next = selected.filter((v) => v !== value);
    return next.length === all.length - 1 ? [] : next;
  }
  const next = [...selected, value];
  return next.length === all.length ? [] : next;
}
